// PURE static-aura derivation. The engine half of the host-coupled
// recomputeStaticAuras orchestrator (controllers/staticAuras.js).
//
// `deriveStaticAuras(board, opts)` walks every on-table aura source (board
// minions/avatars + equipped artifacts), resolves each static effect's scope
// against the board, and RETURNS a per-card derived map. The continuous-effect
// fields the rules layer reads (_auraStatDelta, _auraStatuses,
// _auraSuppressedKeywords, _thresholdSuppression/_thresholdMultiplier,
// _auraSubtypeMap, _auraNameAliases, _grantedTriggers) plus the cross-card
// SIDE-EFFECT intents (control claims, conditional grants, ward grants,
// self-dispel set) that a HOST applies through its own ledger/animation paths.
//
// It touches NO meshes, HUD, badges, animation, ledger or sound. Those stay in
// the thin client applier. It is isomorphic: the same function runs server-side
// (boardShim) to DERIVE the authoritative aura state (closing the SET_CARD_STATS
// anti-cheat hole) and client-side (live meshes) for optimistic render.
//
// `board` is the scope view { meshes, cellAt, getFullCard } (the same shape
// resolveAuraScope / boardShim use). `opts` injects the host's pure helpers:
//   - currentTurn:       'p1' | 'p2' (active-player layer tiebreak)
//   - evaluateCondition: (condition, sourceCard, fullCard, extraOpts?) => bool
//   - resolveAvatarRules: (avatarCard) => { staticAuras } | null (optional)
import { resolveAuraScope, isDisabled } from './scope';
import { OP } from './schema';
import { isCardSilenced, hasKeyword } from '../core/keywordQuery';
import { isWaterSiteAtCell } from '../core/regions';
import { isAutomatonCard } from '../core/typeline';
import { classifyEffect, classifyPowerSub, sortLayered } from '../core/layers';
import { grantWardFromAura } from './ops/wards';
import { countCoveredSitesByController, sumCoveredSiteProvisionByController } from './ops/auraSiteEffects';

// The per-card derived fields, reset fresh every pass so a source leaving play /
// losing scope auto-clears its grants (the array/map is REBUILT, never patched).
function freshDerived() {
  return {
    _auraStatDelta: 0,
    _auraStatuses: [],
    _auraSuppressedKeywords: [],
    _thresholdSuppression: null,
    _thresholdMultiplier: 1,
    _auraSubtypeMap: null,
    _auraNameAliases: null,
    _grantedTriggers: [],
  };
}

export function deriveStaticAuras(board, { currentTurn, evaluateCondition, resolveAvatarRules } = {}) {
  const { meshes, cellAt, getFullCard } = board;
  const evalCond = evaluateCondition || (() => true);

  const resolveScope = (sourceCard, scope, args, overrides) =>
    resolveAuraScope(sourceCard, scope, args, { meshes, cellAt, getFullCard, overrides });

  // cardId -> the LIVE card instance (read off the board view) so apply bodies
  // mutate the same object the host re-reads after derivation.
  const cardById = new Map();
  // cardId -> the fresh derived field bag (returned to the host).
  const derived = new Map();
  for (const [, mesh] of meshes) {
    const card = mesh.userData?.cardInstance;
    if (!card) continue;
    cardById.set(card.id, card);
    derived.set(card.id, freshDerived());
    // Carried cards (equipped artifacts) have no own mesh, so a location scan
    // never sees them. But resolveScope DOES surface them at the bearer's cell
    // (a carried Amethyst Core under Acid Rain is an affected artifact "there").
    // Give each a bag keyed by its id so a status/keyword/stat grant targeting
    // it actually lands (silenced → it stops granting its own aura). The host
    // applies these bags onto the equippedCards entries.
    for (const eq of (card.equippedCards || [])) {
      const eqId = eq?.id ?? eq?.instanceId;
      if (!eqId || derived.has(eqId)) continue;
      cardById.set(eqId, eq);
      derived.set(eqId, freshDerived());
    }
  }

  // Cross-card outputs the HOST applies through its own side-effect paths.
  const controlClaims = new Map();            // cardId -> claiming source's rotated side
  const wantedConditionalGrants = [];         // ledger diff inputs (conditional GRANT_THRESHOLD/MANA)
  const wardGrantTargetIds = new Set();       // cards that received a ward token this pass
  const pendingSelfDispels = new Set();       // sources whose DISPEL_SELF_IF_SCOPE_EMPTY fired

  const d = (id) => derived.get(id);
  const activePlayerRotated = currentTurn === 'p2';

  // Codex Waterbound: "This unit is disabled while not occupying a water
  // site." Derived fresh every pass (a move / flood / dry flips it) into the
  // unit's _auraStatuses, and consulted directly in the source guard below so
  // a beached Waterbound source (Salmon of Knowledge) grants nothing THIS
  // pass. The live card's own _auraStatuses still carry last pass's values.
  const waterboundDisabled = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh.userData?.cardInstance;
    if (!card || card.isSite) continue;
    if (!hasKeyword(card, 'waterbound')) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    const onWater = !!cell && isWaterSiteAtCell(meshes, cellAt, cell.col, cell.row, getFullCard);
    if (!onWater) {
      d(card.id)._auraStatuses.push('disabled');
      waterboundDisabled.add(card.id);
    }
  }

  const collected = [];
  const collectFromSource = (sourceCard, fullSource, overrides) => {
    if (isCardSilenced(sourceCard) || isDisabled(sourceCard)) return;
    if (waterboundDisabled.has(sourceCard.id)) return;
    const baseEntries = fullSource?.automation || [];
    const avatarRuleExtras = sourceCard?.type === 'Avatar' && resolveAvatarRules
      ? (resolveAvatarRules(sourceCard).staticAuras || [])
      : [];
    const entries = avatarRuleExtras.length > 0 ? [...baseEntries, ...avatarRuleExtras] : baseEntries;
    for (const entry of entries) {
      if (entry.trigger !== 'static') continue;
      for (const effect of (entry.effect || [])) {
        const sourceRotated = overrides?.controllerRotated !== undefined
          ? !!overrides.controllerRotated
          : !!sourceCard.rotated;
        collected.push({
          layer: classifyEffect(effect.op, effect.args),
          powerSub: classifyPowerSub(effect.op, effect.args),
          timestamp: typeof sourceCard._enteredRealmAt === 'number' ? sourceCard._enteredRealmAt : 0,
          activePlayerTie: sourceRotated === activePlayerRotated,
          sourceCardId: sourceCard.id,
          sourceCard, fullSource, entry, effect, overrides,
        });
      }
    }
  };

  const applyOne = ({ sourceCard, fullSource, entry, effect, overrides }) => {
    if (entry.condition && effect.op !== OP.GRANT_THRESHOLD && effect.op !== OP.GRANT_MANA) {
      const bearer = overrides?.bearerCard || null;
      if (!evalCond(entry.condition, sourceCard, fullSource, { bearer })) return;
    }
    const isSelfBearerScope = entry.scope?.where === 'self_bearer';
    const selfBearer = isSelfBearerScope ? (overrides?.bearerCard || null) : null;
    if (isSelfBearerScope && !selfBearer) return;

    if (effect.op === OP.GRANT_STAT) {
      const targets = selfBearer ? [selfBearer] : resolveScope(sourceCard, entry.scope, effect.args, overrides);
      for (const t of targets) { const b = d(t.id); if (b) b._auraStatDelta += (effect.args?.delta || 0); }
    } else if (effect.op === OP.GRANT_STAT_PER_OTHER_IN_SCOPE) {
      const count = effect.args?.count || {};
      const countMatches = resolveScope(sourceCard, entry.scope,
        { filter: count.filter || {}, exclude_self: count.exclude_self !== false }, overrides);
      const n = countMatches.length;
      if (n > 0) {
        const targets = resolveScope(sourceCard, entry.scope,
          { filter: effect.args?.target_filter || {}, exclude_self: true }, overrides);
        const delta = (effect.args?.delta || 0) * n;
        for (const t of targets) { const b = d(t.id); if (b) b._auraStatDelta += delta; }
      }
    } else if (effect.op === OP.DISPEL_SELF_IF_SCOPE_EMPTY) {
      if (!pendingSelfDispels.has(sourceCard.id)) {
        const matches = resolveScope(sourceCard, entry.scope,
          { filter: effect.args?.filter || {}, exclude_self: true }, overrides);
        if (matches.length === 0) pendingSelfDispels.add(sourceCard.id);
      }
    } else if (effect.op === OP.GRANT_STAT_PER_EACH_SELF) {
      const count = effect.args?.count || {};
      const matches = resolveScope(sourceCard, { where: count.scope },
        { filter: count.filter, exclude_self: count.exclude_self }, overrides);
      const total = (effect.args?.delta || 0) * matches.length;
      const b = d(sourceCard.id); if (b) b._auraStatDelta += total;
    } else if (effect.op === OP.GRANT_KEYWORD) {
      const targets = selfBearer ? [selfBearer] : resolveScope(sourceCard, entry.scope, effect.args, overrides);
      const kw = effect.args?.keyword;
      if (!kw) return;
      if (kw === 'ward') {
        for (const t of targets) { if (grantWardFromAura(t, sourceCard.id)) wardGrantTargetIds.add(t.id); }
      } else {
        for (const t of targets) {
          const b = d(t.id); if (!b) continue;
          if (!b._auraStatuses.includes(kw)) b._auraStatuses.push(kw);
        }
      }
    } else if (effect.op === OP.TRANSFORM_SUBTYPES) {
      const map = effect.args?.map;
      if (!map) return;
      const targets = resolveScope(sourceCard, entry.scope,
        { filter: effect.args?.filter || {}, exclude_self: false }, overrides);
      for (const t of targets) { const b = d(t.id); if (b) b._auraSubtypeMap = { ...(b._auraSubtypeMap || {}), ...map }; }
    } else if (effect.op === OP.ADD_NAME_ALIAS) {
      const alias = effect.args?.name;
      if (!alias) return;
      const targets = resolveScope(sourceCard, entry.scope,
        { filter: effect.args?.filter || {}, exclude_self: false }, overrides);
      for (const t of targets) {
        const b = d(t.id); if (!b) continue;
        if (!b._auraNameAliases) b._auraNameAliases = [];
        if (!b._auraNameAliases.includes(alias)) b._auraNameAliases.push(alias);
      }
    } else if (effect.op === OP.MULTIPLY_THRESHOLDS_ON_SCOPE) {
      const factor = effect.args?.factor;
      if (!factor || factor === 1) return;
      const filter = effect.args?.filter || { type: 'Site' };
      const targets = resolveScope(sourceCard, entry.scope, { filter, exclude_self: true }, overrides);
      for (const t of targets) { const b = d(t.id); if (b) b._thresholdMultiplier *= factor; }
    } else if (effect.op === OP.SUPPRESS_THRESHOLD_ON_SCOPE) {
      const element = effect.args?.element;
      if (!element) return;
      const filter = effect.args?.filter || { type: 'Site' };
      const targets = resolveScope(sourceCard, entry.scope, { filter, exclude_self: true }, overrides);
      for (const t of targets) {
        const b = d(t.id); if (!b) continue;
        if (!b._thresholdSuppression) b._thresholdSuppression = {};
        b._thresholdSuppression[element] = true;
      }
    } else if (effect.op === OP.REVOKE_KEYWORD) {
      const targets = selfBearer ? [selfBearer] : resolveScope(sourceCard, entry.scope, effect.args, overrides);
      const kw = effect.args?.keyword;
      if (!kw) return;
      for (const t of targets) {
        const b = d(t.id); if (!b) continue;
        if (!b._auraSuppressedKeywords.includes(kw)) b._auraSuppressedKeywords.push(kw);
      }
    } else if ((effect.op === OP.GRANT_THRESHOLD || effect.op === OP.GRANT_MANA) && entry.condition) {
      const controller = overrides?.controllerRotated !== undefined
        ? (overrides.controllerRotated ? 'p2' : 'p1')
        : (sourceCard.rotated ? 'p2' : 'p1');
      if (evalCond(entry.condition, sourceCard, fullSource)) {
        wantedConditionalGrants.push({ sourceCard, controller, effect, entry });
      }
    } else if (effect.op === OP.GRANT_MANA_PER_COVERED_SITE) {
      // Abundance grants a FLAT +1 per covered site; Sow the Earth DOUBLES each
      // covered site's actual provision (args.double). The extra equals what
      // the site already provides, so a covered Shrine reaches 4, not 3.
      const { allied, enemy } = effect.args?.double
        ? sumCoveredSiteProvisionByController(sourceCard, meshes, cellAt, getFullCard)
        : countCoveredSitesByController(sourceCard, meshes, cellAt);
      const sourceRotated = overrides?.controllerRotated !== undefined
        ? !!overrides.controllerRotated : !!sourceCard.rotated;
      const sourceCtl = sourceRotated ? 'p2' : 'p1';
      const oppCtl = sourceRotated ? 'p1' : 'p2';
      if (allied > 0) {
        wantedConditionalGrants.push({ sourceCard, controller: sourceCtl,
          effect: { op: OP.GRANT_MANA, args: { amount: allied } }, entry });
      }
      if (enemy > 0) {
        wantedConditionalGrants.push({ sourceCard, controller: oppCtl,
          effect: { op: OP.GRANT_MANA, args: { amount: enemy } }, entry });
      }
    } else if (effect.op === OP.GRANT_CONTROL) {
      const baseRotated = overrides?.controllerRotated !== undefined
        ? overrides.controllerRotated : sourceCard.rotated;
      const claimRotated = controlClaims.has(sourceCard.id) ? controlClaims.get(sourceCard.id) : baseRotated;
      const targets = resolveScope(sourceCard, entry.scope,
        { filter: effect.args?.filter || {}, exclude_self: true }, overrides);
      for (const t of targets) controlClaims.set(t.id, claimRotated);
    } else if (effect.op === OP.GRANT_TRIGGER_TO_SCOPE) {
      const triggerEntry = effect.args?.trigger_entry;
      if (triggerEntry) {
        const receivers = resolveScope(sourceCard, entry.scope,
          { filter: entry.scope?.filter || {}, exclude_self: true }, overrides);
        for (const r of receivers) {
          const b = d(r.id); if (!b) continue;
          b._grantedTriggers.push({ ...triggerEntry, _sourceCardId: sourceCard.id });
        }
      }
    }
  };

  // Phase 1. Board-resident sources (artifacts only contribute while equipped,
  // except Automatons which count as minions).
  for (const [, sourceMesh] of meshes) {
    const sourceCard = sourceMesh.userData?.cardInstance;
    if (!sourceCard) continue;
    if (isDisabled(sourceCard)) continue;
    const fullSource = getFullCard(sourceCard.cardId);
    if (fullSource?.type === 'Artifact' && !isAutomatonCard(fullSource)) continue;
    collectFromSource(sourceCard, fullSource);
  }

  // Phase 1b. Equipped artifacts (anchored on their host).
  for (const [, hostMesh] of meshes) {
    const hostCard = hostMesh.userData?.cardInstance;
    const equipment = hostCard?.equippedCards;
    if (!equipment || equipment.length === 0) continue;
    if (isDisabled(hostCard)) continue;
    const hostPos = { x: hostMesh.position.x, z: hostMesh.position.z };
    for (const equipped of equipment) {
      const fullEquip = getFullCard(equipped.cardId);
      if (fullEquip?.type !== 'Artifact') continue;
      collectFromSource(equipped, fullEquip, {
        sourcePos: hostPos, controllerRotated: hostCard.rotated, bearerCard: hostCard,
      });
    }
  }

  // Phase 2. Layer-then-timestamp ordered apply; each effect reads the running
  // cumulative state (e.g. the second King sees the first King's claim).
  for (const tuple of sortLayered(collected)) applyOne(tuple);

  return { derived, cardById, controlClaims, wantedConditionalGrants, wardGrantTargetIds, pendingSelfDispels };
}

// Shallow value-equality for the derived field shapes (number | null | array |
// plain map). Used so applyDerivedToCard only WRITES a field that actually
// changed. On the server this keeps the post-reduce derivation from emitting a
// redundant patch on every command, so a stable board produces an empty diff.
function valuesEqual(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  const aObj = a && typeof a === 'object' && !Array.isArray(a);
  const bObj = b && typeof b === 'object' && !Array.isArray(b);
  if (aObj && bObj) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
  }
  return false;
}

// Copy a derived field bag onto a live card instance. The host applier and the
// server derivation hook share this so the write is identical on both sides.
// Only fields that changed are written (idempotent: re-deriving a stable board
// is a no-op), which on the server keeps the diff minimal.
export function applyDerivedToCard(card, bag) {
  if (!card || !bag) return;
  for (const key of DERIVED_KEYS) {
    if (!valuesEqual(card[key], bag[key])) card[key] = bag[key];
  }
  // A manual deny of Disabled answers one grant (the radial's "off" on a unit
  // under A Midsummer Night's Dream, or beached with Waterbound). Unlike a
  // printed keyword, that grant is transient, and the deny list otherwise
  // lives until the card leaves play, so it would silently cancel every
  // FUTURE disable of this unit. Drop it as soon as no grant remains; a new
  // grant then disables again and the player can deny it afresh.
  const denied = card._suppressedKeywords;
  if (Array.isArray(denied) && denied.includes('disabled')
      && !(bag._auraStatuses || []).includes('disabled')
      && !(card._printedKeywords || []).includes('disabled')) {
    card._suppressedKeywords = denied.filter((k) => k !== 'disabled');
  }
}

const DERIVED_KEYS = [
  '_auraStatDelta', '_auraStatuses', '_auraSuppressedKeywords',
  '_thresholdSuppression', '_thresholdMultiplier', '_auraSubtypeMap',
  '_auraNameAliases', '_grantedTriggers',
];
